import Vue from 'vue'

/**
 * Config Bus — strato di persistenza (Sprint 16, epic REFACTOR-030).
 *
 * Il backend `/store` memorizza un BLOB unico per (type, key): non esiste patch
 * per-chiave. Qui simuliamo la granularità lato client con un **flush debounced**:
 * più `commitChange` ravvicinati (es. filtro + sort + pin) vengono coalescati in
 * UN solo POST del blob completo. Su fallimento → ROLLBACK delle chiavi dirty.
 *
 * Usa `Vue.prototype.$fetch` / `$env` (già disponibili fuori dai componenti, vedi
 * plugins/Caller.js e AuthProcess.js) → nessun bisogno di passare una vueInstance.
 *
 * Vedi `docs/architecture/config-bus.md` §6.
 */

const DEBOUNCE_MS = 600

// Timer per-scope a livello di modulo (singleton): un commitChange ri-arma il
// timer del proprio scope, estendendo la finestra di coalescing del burst.
const timers = { user: null, company: null }

function blobFor(state, scope) {
    return scope === 'company' ? { ...state.company } : { ...state.user }
}

/** Ri-arma il flush debounced per lo scope dato. */
export function scheduleFlush(ctx, scope) {
    if (timers[scope]) clearTimeout(timers[scope])
    timers[scope] = setTimeout(() => {
        timers[scope] = null
        flush(ctx, scope)
    }, DEBOUNCE_MS)
}

/** Esegue subito il flush pendente (es. prima di unload). Ritorna la promise del POST. */
export function flushNow(ctx, scope) {
    if (timers[scope]) {
        clearTimeout(timers[scope])
        timers[scope] = null
    }
    return flush(ctx, scope)
}

/**
 * Flush di TUTTI gli scope con un flush in volo (timer armato). Usato dall'hook di
 * unload (GeoFleet.vue): copre `user` E `company` in un colpo, senza POST inutili
 * sugli scope senza writes pendenti. Robusto ai writer company (es. `poiSettings`
 * da PoiManagerPanel) migrati a `commitChange`, che armano il timer `company` —
 * non coperto da un flush user-only.
 */
export function flushPending(ctx) {
    return Promise.all(
        Object.keys(timers)
            .filter((scope) => timers[scope])
            .map((scope) => flushNow(ctx, scope))
    )
}

async function flush({ commit, state }, scope) {
    const body = {
        type: scope,
        key: 'geofleet',
        data: blobFor(state, scope)
    }

    try {
        const res = await Vue.prototype.$fetch(Vue.prototype.$env.GOLIA_API + '/store', {
            method: 'POST',
            body: JSON.stringify(body),
            // `keepalive` permette al POST di sopravvivere al teardown della pagina: il
            // flush può partire da un `beforeunload` (GeoFleet.vue) quando un burst è
            // ancora nella finestra di debounce ~600ms. Senza, il browser annullerebbe
            // la richiesta in volo all'unload → modifica persa. Blob config piccolo,
            // ben sotto il limite di 64KB di keepalive.
            keepalive: true
        })
        await res.json()
        commit('CLEAR_DIRTY', scope)
        commit('SET_STATUS', { saving: false, error: null })
    } catch (error) {
        // Fallimento del POST: ripristina le chiavi cambiate al loro snapshot
        // confermato e segnala l'errore. La notifica utente (toast) viene
        // cablata in Sprint 19 (UX) leggendo `status.error`.
        commit('ROLLBACK', scope)
        commit('SET_STATUS', { saving: false, error })
    }
}
